import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BarChart3, TrendingUp, Clock, Users, DollarSign, ShoppingCart, Home, CalendarDays } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import api, { formatPrice } from "@/lib/api";

const PRESETS = [
  { label: "Сегодня", value: "day" },
  { label: "Неделя", value: "week" },
  { label: "Месяц", value: "month" },
  { label: "Год", value: "year" },
];

const fmtDate = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fmtDisplay = (str) => {
  if (!str) return "";
  const [y, m, d] = str.split("-");
  return `${d}.${m}.${y}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border p-2.5 rounded-sm shadow-md text-xs">
      <p className="text-foreground font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: {typeof p.value === "number" ? formatPrice(p.value) : p.value}
        </p>
      ))}
    </div>
  );
};

const CountTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border p-2.5 rounded-sm shadow-md text-xs">
      <p className="text-foreground font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

export default function AdminAnalytics() {
  const [preset, setPreset] = useState("month");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [calRange, setCalRange] = useState({ from: undefined, to: undefined });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    let url;
    if (dateFrom && dateTo) {
      url = `/admin/analytics?date_from=${dateFrom}&date_to=${dateTo}`;
    } else {
      url = `/admin/analytics?period=${preset}`;
    }
    api.get(url)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [preset, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const selectPreset = (v) => {
    setPreset(v);
    setDateFrom(null);
    setDateTo(null);
  };

  const applyRange = () => {
    if (calRange.from) {
      const from = fmtDate(calRange.from);
      const to = calRange.to ? fmtDate(calRange.to) : from;
      setDateFrom(from);
      setDateTo(to);
      setPreset("");
      setCalOpen(false);
    }
  };

  if (loading) return <div className="text-muted-foreground" data-testid="analytics-loading">Загрузка аналитики...</div>;
  if (!data) return <div className="text-muted-foreground">Ошибка загрузки</div>;

  const s = data.summary || {};
  const rangeLabel = (dateFrom && dateTo)
    ? (dateFrom === dateTo ? fmtDisplay(dateFrom) : `${fmtDisplay(dateFrom)} — ${fmtDisplay(dateTo)}`)
    : null;

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-serif text-3xl flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-primary" />
          Аналитика
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1 bg-muted p-1 rounded-sm" data-testid="period-selector">
            {PRESETS.map((p) => (
              <Button
                key={p.value}
                variant={preset === p.value && !dateFrom ? "default" : "ghost"}
                size="sm"
                onClick={() => selectPreset(p.value)}
                className={`rounded-none text-xs ${preset === p.value && !dateFrom ? "bg-primary text-white" : ""}`}
                data-testid={`period-${p.value}`}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={dateFrom ? "default" : "outline"}
                size="sm"
                className={`rounded-none text-xs gap-1.5 ${dateFrom ? "bg-primary text-white" : "border-border"}`}
                data-testid="calendar-picker-btn"
              >
                <CalendarDays className="w-3.5 h-3.5" />
                {rangeLabel || "Выбрать даты"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-card border-border" align="end">
              <div className="p-3">
                <Calendar
                  mode="range"
                  selected={calRange}
                  onSelect={(range) => setCalRange(range || { from: undefined, to: undefined })}
                  numberOfMonths={1}
                  disabled={{ after: new Date() }}
                  data-testid="analytics-calendar"
                />
                <div className="flex items-center justify-between mt-2 px-1">
                  <span className="text-xs text-muted-foreground">
                    {calRange.from ? fmtDisplay(fmtDate(calRange.from)) : "—"}
                    {calRange.to ? ` — ${fmtDisplay(fmtDate(calRange.to))}` : ""}
                  </span>
                  <Button
                    size="sm"
                    onClick={applyRange}
                    disabled={!calRange.from}
                    className="rounded-none text-xs bg-primary text-white"
                    data-testid="apply-date-range-btn"
                  >
                    Применить
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Date range indicator */}
      {data.date_from && (
        <p className="text-xs text-muted-foreground mb-4" data-testid="date-range-label">
          Период: {fmtDisplay(data.date_from)}{data.date_from !== data.date_to ? ` — ${fmtDisplay(data.date_to)}` : ""}
        </p>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="analytics-summary">
        <div className="bg-card border border-border p-4 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-green-600" />
            <span className="text-xs text-muted-foreground">Общая выручка</span>
          </div>
          <p className="text-2xl font-serif text-primary">{formatPrice(s.total_revenue || 0)}</p>
        </div>
        <div className="bg-card border border-border p-4 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <Home className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-muted-foreground">Бронирований</span>
          </div>
          <p className="text-2xl font-serif">{s.total_bookings || 0}</p>
          <p className="text-xs text-muted-foreground">Ср. чек: {formatPrice(s.avg_booking || 0)}</p>
        </div>
        <div className="bg-card border border-border p-4 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <ShoppingCart className="w-4 h-4 text-orange-500" />
            <span className="text-xs text-muted-foreground">Заказов</span>
          </div>
          <p className="text-2xl font-serif">{s.total_orders || 0}</p>
          <p className="text-xs text-muted-foreground">Ср. чек: {formatPrice(s.avg_order || 0)}</p>
        </div>
        <div className="bg-card border border-border p-4 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            <span className="text-xs text-muted-foreground">Выручка (заказы)</span>
          </div>
          <p className="text-2xl font-serif text-blue-600">{formatPrice(s.order_revenue || 0)}</p>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="bg-card border border-border p-5 mb-6 rounded-sm">
        <h2 className="font-sans font-semibold text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-primary" /> Динамика выручки
        </h2>
        {data.revenue_chart?.length > 0 ? (
          <div data-testid="revenue-chart" className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.revenue_chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(33 20% 88%)" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(24 10% 45%)" }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(24 10% 45%)" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}к`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="bookings" name="Брони" fill="#5EC75E" radius={[2, 2, 0, 0]} />
                <Bar dataKey="orders" name="Заказы" fill="#C7A96B" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm text-center py-8">Нет данных за выбранный период</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top Houses */}
        <div className="bg-card border border-border p-5 rounded-sm">
          <h2 className="font-sans font-semibold text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
            <Home className="w-4 h-4 text-primary" /> Топ домиков
          </h2>
          {data.top_houses?.length > 0 ? (
            <div data-testid="top-houses-chart" className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.top_houses.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(33 20% 88%)" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(24 10% 45%)" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}к`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "hsl(24 10% 45%)" }} width={100} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="total" name="Выручка" fill="#5EC75E" radius={[0, 2, 2, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm text-center py-8">Нет данных</p>
          )}
        </div>

        {/* Popular Items */}
        <div className="bg-card border border-border p-5 rounded-sm">
          <h2 className="font-sans font-semibold text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-primary" /> Популярные блюда
          </h2>
          {data.popular_items?.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="popular-items">
              {data.popular_items.map((item, i) => (
                <div key={i} className="flex items-center justify-between bg-background border border-border p-2.5 rounded-sm text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 flex items-center justify-center bg-primary/10 text-primary text-xs font-semibold rounded-full">{i + 1}</span>
                    <span>{item.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-primary font-semibold">{item.count} шт</span>
                    <p className="text-[10px] text-muted-foreground">{formatPrice(item.revenue)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm text-center py-8">Нет данных</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Booking by Hour */}
        <div className="bg-card border border-border p-5 rounded-sm">
          <h2 className="font-sans font-semibold text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" /> Бронирования по часам
          </h2>
          {data.hour_chart?.length > 0 ? (
            <div data-testid="hour-chart" className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.hour_chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(33 20% 88%)" />
                  <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "hsl(24 10% 45%)" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(24 10% 45%)" }} />
                  <Tooltip content={<CountTooltip />} />
                  <Bar dataKey="count" name="Брони" fill="#C7A96B" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm text-center py-8">Нет данных</p>
          )}
        </div>

        {/* Client Growth */}
        <div className="bg-card border border-border p-5 rounded-sm">
          <h2 className="font-sans font-semibold text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" /> Рост клиентской базы
          </h2>
          {data.client_growth?.length > 0 ? (
            <div data-testid="client-growth-chart" className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.client_growth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(33 20% 88%)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(24 10% 45%)" }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(24 10% 45%)" }} />
                  <Tooltip content={<CountTooltip />} />
                  <Line type="monotone" dataKey="new" name="Новые" stroke="#5EC75E" strokeWidth={2} dot={{ r: 3, fill: "#5EC75E" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm text-center py-8">Нет данных</p>
          )}
        </div>
      </div>
    </div>
  );
}
